/**
 * DecisionTable — queue #667 §UAT-2026-07-09-R667 §R667-4.
 *
 * 선택 가이드 뷰에서 선택된 비교군 1개의 비교표 — 열=후보(코드+라벨), 행=비교 축(적합
 * 시나리오·운영 비용·인프라 요구·전제 조건·성숙도). 성숙도 열은 §R643 `deriveMaturity` 를
 * 그대로 파생하는 `MaturityBadge` 컴포넌트를 재사용한다(중복 저장·재구현 0). 권장 후보
 * (`recommendedOption`)는 뱃지 + 열 배경으로 강조하고, `highlightCode`(모듈 상세 '대안과
 * 비교' 진입점 딥링크)가 있으면 해당 후보 열을 추가로 테두리 강조한다. 하단에 관련
 * 레시피/함정 딥링크(있을 때만)를 렌더한다.
 */
import { useTranslation } from 'react-i18next';
import { StatusPill } from '@ui-kit';
import type { DecisionGuide } from '../data/decision-guides';
import { recommendedOption } from '../data/decision-guides';
import type { ModuleNode } from '../types/showcase';
import { MaturityBadge } from './MaturityBadge';

const headerCellStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: 'var(--spacing-2) var(--spacing-3)',
  borderBottom: '1px solid var(--color-surface-border)',
  verticalAlign: 'top',
};

const axisCellStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  borderBottom: '1px solid var(--color-surface-border)',
  fontWeight: 600,
  color: 'var(--color-text-muted)',
  fontSize: 'var(--font-size-xs)',
  whiteSpace: 'nowrap',
  verticalAlign: 'top',
};

const bodyCellStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  borderBottom: '1px solid var(--color-surface-border)',
  color: 'var(--color-text-secondary)',
  fontSize: 'var(--font-size-sm)',
  verticalAlign: 'top',
};

const blockStyle: React.CSSProperties = {
  marginTop: 'var(--spacing-4)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  padding: 'var(--spacing-3) var(--spacing-4)',
  background: 'var(--color-surface-elevated)',
};

const blockTitleStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  fontWeight: 700,
  color: 'var(--color-text-primary)',
  margin: '0 0 var(--spacing-2)',
};

interface Props {
  guide: DecisionGuide;
  /** 성숙도 파생용 카탈로그 노드(§R643 deriveMaturity 는 ModuleNode 를 입력으로 받는다) */
  nodes: ModuleNode[];
  /** 모듈 상세 '대안과 비교' 진입점 딥링크(`?highlight=<code>`)로 도달한 경우 강조할 후보 코드 */
  highlightCode?: string | null;
  /** 후보 코드 클릭 — 카탈로그 뷰로 전환 + 해당 노드 선택 */
  onSelectModule: (code: string) => void;
  /** 관련 레시피 딥링크 클릭 */
  onSelectRecipe: (id: string) => void;
  /** 관련 함정 딥링크 클릭 */
  onSelectPitfall: (id: string) => void;
}

export function DecisionTable({ guide, nodes, highlightCode, onSelectModule, onSelectRecipe, onSelectPitfall }: Props) {
  const { t } = useTranslation();
  const recommended = recommendedOption(guide);

  return (
    <section aria-label={t('showcase.decision.table.ariaLabel', { guide: t(guide.titleKey) })} data-testid={`decision-table-${guide.id}`}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={headerCellStyle} scope="col" />
              {guide.options.map((opt) => {
                const isRecommended = opt.code === recommended.code;
                const isHighlighted = !!highlightCode && opt.code === highlightCode;
                return (
                  <th
                    key={opt.code}
                    scope="col"
                    style={{
                      ...headerCellStyle,
                      background: isRecommended ? 'var(--color-surface-muted)' : undefined,
                      outline: isHighlighted ? '2px solid var(--color-brand-primary)' : undefined,
                      outlineOffset: isHighlighted ? '-2px' : undefined,
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          onClick={() => onSelectModule(opt.code)}
                          aria-label={t('showcase.decision.table.moduleJumpAria', { code: opt.code })}
                          style={{
                            fontFamily: 'var(--font-family-mono)',
                            fontSize: 'var(--font-size-xs)',
                            background: 'var(--color-surface-background)',
                            border: '1px solid var(--color-brand-primary)',
                            color: 'var(--color-brand-primary)',
                            borderRadius: 'var(--radius-sm)',
                            padding: '2px var(--spacing-2)',
                            cursor: 'pointer',
                          }}
                        >
                          {opt.code}
                        </button>
                        {isRecommended && <StatusPill tone="success">{t('showcase.decision.recommended')}</StatusPill>}
                      </div>
                      <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 700, color: 'var(--color-text-primary)' }}>
                        {t(opt.labelKey)}
                      </span>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            <tr>
              <th scope="row" style={axisCellStyle}>
                {t('showcase.decision.axis.scenario')}
              </th>
              {guide.options.map((opt) => (
                <td key={opt.code} style={bodyCellStyle}>
                  {t(opt.scenarioKey)}
                </td>
              ))}
            </tr>
            <tr>
              <th scope="row" style={axisCellStyle}>
                {t('showcase.decision.axis.opsCost')}
              </th>
              {guide.options.map((opt) => (
                <td key={opt.code} style={bodyCellStyle}>
                  {t(opt.opsCostKey)}
                </td>
              ))}
            </tr>
            <tr>
              <th scope="row" style={axisCellStyle}>
                {t('showcase.decision.axis.infra')}
              </th>
              {guide.options.map((opt) => (
                <td key={opt.code} style={bodyCellStyle}>
                  {t(opt.infraKey)}
                </td>
              ))}
            </tr>
            <tr>
              <th scope="row" style={axisCellStyle}>
                {t('showcase.decision.axis.prerequisite')}
              </th>
              {guide.options.map((opt) => (
                <td key={opt.code} style={bodyCellStyle}>
                  {t(opt.prerequisiteKey)}
                </td>
              ))}
            </tr>
            <tr>
              <th scope="row" style={axisCellStyle}>
                {t('showcase.decision.axis.maturity')}
              </th>
              {guide.options.map((opt) => {
                const node = nodes.find((n) => n.code === opt.code);
                return (
                  <td key={opt.code} style={bodyCellStyle}>
                    {node ? <MaturityBadge node={node} /> : '—'}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>

      {((guide.recipeIds && guide.recipeIds.length > 0) || (guide.pitfallIds && guide.pitfallIds.length > 0)) && (
        <div style={blockStyle}>
          {guide.recipeIds && guide.recipeIds.length > 0 && (
            <div style={{ marginBottom: guide.pitfallIds && guide.pitfallIds.length > 0 ? 'var(--spacing-3)' : 0 }}>
              <p style={blockTitleStyle}>{t('showcase.decision.related.recipes')}</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-2)' }}>
                {guide.recipeIds.map((id) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => onSelectRecipe(id)}
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      background: 'var(--color-surface-background)',
                      border: '1px solid var(--color-surface-border)',
                      color: 'var(--color-brand-primary)',
                      borderRadius: 'var(--radius-sm)',
                      padding: '2px var(--spacing-2)',
                      cursor: 'pointer',
                      fontFamily: 'var(--font-family-sans)',
                    }}
                  >
                    {id}
                  </button>
                ))}
              </div>
            </div>
          )}
          {guide.pitfallIds && guide.pitfallIds.length > 0 && (
            <div>
              <p style={blockTitleStyle}>{t('showcase.decision.related.pitfalls')}</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-2)' }}>
                {guide.pitfallIds.map((id) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => onSelectPitfall(id)}
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      background: 'var(--color-surface-background)',
                      border: '1px solid var(--color-surface-border)',
                      color: 'var(--color-brand-primary)',
                      borderRadius: 'var(--radius-sm)',
                      padding: '2px var(--spacing-2)',
                      cursor: 'pointer',
                      fontFamily: 'var(--font-family-sans)',
                    }}
                  >
                    {id}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
